/**
 * FieldGate — wrapper for any required input on a wizard step.
 * Feb 2026 — bright-red highlight when the field is empty AND the user
 * tried to advance, plus a "Not needed" chip so truly-optional fields
 * can be waived without ever leaving the step.
 *
 * Usage:
 *   <FieldGate fieldKey="employeeAddress1" missingFields={mf} skippedFields={sf}
 *              onToggleSkip={onToggleSkip} attemptedNext={att}>
 *     <Input value={...} onChange={...} />
 *   </FieldGate>
 *
 * The wrapper reads its own children — the caller keeps full control of
 * the input's styling, we just paint a red ring around whatever's inside
 * and drop the chip in the top-right corner of the wrapper.
 */
import React from 'react';

export default function FieldGate({
  fieldKey,
  missingFields = [],
  skippedFields = {},
  onToggleSkip,
  attemptedNext,
  className = '',
  children,
  allowSkip = true,
}) {
  const isMissing = attemptedNext && missingFields.some((m) => m.key === fieldKey);
  const isSkipped = !!skippedFields[fieldKey];

  return (
    <div className={`relative ${className}`} data-testid={`fieldgate-${fieldKey}`}>
      <div
        className={`transition ${
          isMissing
            ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-zinc-900 rounded-md animate-pulse-slow'
            : ''
        } ${isSkipped ? 'opacity-60' : ''}`}
      >
        {children}
      </div>

      {allowSkip && onToggleSkip && (
        <button
          type="button"
          onClick={() => onToggleSkip(fieldKey)}
          className={`absolute -top-1 right-0 text-[10px] px-1.5 py-0.5 rounded-full transition ${
            isSkipped
              ? 'bg-amber-500 text-black font-bold hover:bg-amber-400'
              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-white'
          }`}
          title={isSkipped ? 'Un-skip: mark this required again' : 'Skip: this field is not needed'}
          data-testid={`fieldgate-skip-${fieldKey}`}
        >
          {isSkipped ? '✓ Not needed' : 'Not needed'}
        </button>
      )}

      {isMissing && (
        <p className="text-red-400 text-[10px] mt-0.5 font-medium" data-testid={`fieldgate-error-${fieldKey}`}>
          Required — fill in or tap "Not needed"
        </p>
      )}
    </div>
  );
}
